import { openDB } from 'idb';
import {
    EMPLOYEE_STORE,
    VENDOR_STORE,
    TRUCK_STORE,
    INSTRUCTION_STORE,
    TOOLBOXTALK_STORE,
    TRAINING_DUE_STORE,
    COST_CODE_STORE,
    GL_CODE_STORE,
    SIGNED_DOCS_STORE,
    WEEKLY_INSTRUCTION_STORE,
    WEEK_LIST_STORE,
    MEP_FORM_STORE,
    FT_FORM_STORE,
    TRAINING_STORE,
    DB_VERSION,
    DB_NAME,
    CC_STORE,
    CODE_GROUP_STORE,
    JOB_STORE,
    TRAINING_DUE_TEXT_STORE,
    TRAINING_DUE_FORM_STORE,
    USER_STORE,
    WELDING_FORM_STORE,
    INI_STORE,
    SSD_STORE,
    CONDITIONS_STORE,
    ENERGY_STORE,
    DOCS_PERMIT_STORE,
    EVALUATION_OF_WORK_STORE,
    JOB_TASK_STORE,
    WORK_ACTIVITIES_STORE,
    JHA_STORE,
    TEMPS_STORE,
    ATTEND_CODES_STORE,
    TEMP_AGENCY_STORE,
    TEMP_EXHIBIT_B_STORE,
    TRAINER_STORE,
    DAILY_LOG_STORE,
    INCIDENT_STORE,
    WITNESS_STORE,
    UNIFIED_FORM_STORE,
    TBT_SUBMISSION_STORE,
    PIPE_HYDRO_TEST_STORE,
    VESSEL_HYDRO_TEST_STORE,
    SERVICE_LEAK_TEST_STORE,
    WELD_MAP_STORE,
    PNEUMATIC_TEST_STORE,
    TORQUE_MAP_STORE,
    GAS_LINE_INSPECTION_STORE,
    MEGGER_TEST_STORE,
    TERMINAL_TORQUE_TEST_STORE,
    PANEL_SHOP_QC_STORE,
    JOB_SITE_SAFETY_INSPECTION_STORE,
    JSSI_SCHEMA_STORE,
    BUS_INSPECTION_STORE,
    CABLE_WIRING_SCHEDULE_STORE,
    ENERGIZED_WORK_PERMIT_STORE,
    CHANGE_ORDER_STORE,
    SOW_STORE,
    OPERATOR_PRACTICAL_EVALUATION_STORE,
    AERIAL_SCISSOR_LIFT_OPERATOR_STORE,
    SPOTTER_TRAINING_STORE,
    PACKING_SLIP_STORE,
    CSEP_STORE,
    LTT_STORE,
    CONTINUOUS_IMPROVEMENT_STORE,
    WARRANTY_STORE,
    EQUIPMENT_STORE,
    EQUIP_LOCATION_STORE,
    PURCHASE_ORDER_STORE,
    PO_EDITS_STORE,
    PRACTICAL_EVALUATION_TYPE_STORE,
    PRACTICAL_EVALUATION_STORE,
    TOOL_INVENTORY_STORE,
    TOOL_STORE,
    TOOL_CALIBRATION_STORE,
    TOOL_DOCS_STORE,
    TOOL_ASSIGNMENT_STORE,
    GAS_MONITOR_TEST_STORE,
    SRD_INSPECTION_STORE,
    FALL_ARREST_RESCUE_PLAN_STORE,
    WELDER_QUALIFICATION_RELEASE_STORE,
    LIFT_EVALUATION_STORE,
    LIFT_TRUCK_PM_STORE,
    JHA_DOCUMENT_TRIGGER_STORE,
    TRAINING_COURSE_STORE,
    TRAINING_QUIZ_STORE,
    TRAINING_SESSION_STORE,
    TRAINING_OUTBOX_STORE,
    SAFETY_SCHEDULE_STORE,
    SAFETY_VISIT_OUTBOX_STORE,
    EXT_EQUIPMENT_INSPECTION_STORE,
    EXT_EQUIPMENT_DAILY_CHECK_STORE,
    ALT_JOB_STORE,
    TIMESHEET_RECORD_STORE,
    DAILY_JOB_NOTE_STORE,
    SIGNED_TIMESHEET_STORE,
    SELECTED_EMPLOYEES_STORE,
    SELECTED_JOBS_STORE,
    SDS_STORE
} from './constants';

// Кэшируем соединение к БД
let currentDb = null;
let keepAliveTimer = null;

/**
 * Проверка что соединение к БД еще живое
 * @returns {Promise<boolean>}
 */
async function checkConnection() {
    if (!currentDb) return false;
    try {
        // Пробуем выполнить простую транзакцию для проверки
        const tx = currentDb.transaction([EMPLOYEE_STORE], 'readonly');
        await tx.done;
        return true;
    } catch (error) {
        console.warn('[DB] Connection check failed:', error);
        return false;
    }
}

/**
 * Открытие БД с защитой от гонки.
 *
 * На старте initializeDB зовут разом полтора десятка сервисов. Без этой обёртки
 * каждый открывал СВОЁ соединение, и при смене DB_VERSION апгрейд вставал
 * намертво: upgradeneeded ждёт закрытия остальных соединений, а они в этот
 * момент сами ждут открытия. currentDb тогда ещё null, поэтому blocking() тоже
 * ничего не закрывал. Проявлялось только на версии — приложение просто зависало.
 */
let openingPromise = null;

export const initializeDB = async (forceReopen = false) => {
    if (currentDb && !forceReopen) {
        const isAlive = await checkConnection();
        if (isAlive) {
            return currentDb;
        }
        console.log('[DB] Connection dead, reopening...');
        currentDb = null;
    }

    // Открытие уже идёт — ждём его, а не заводим второе соединение.
    if (openingPromise) {
        return openingPromise;
    }

    openingPromise = openDatabaseInternal(forceReopen);
    try {
        return await openingPromise;
    } finally {
        openingPromise = null;
    }
};

const openDatabaseInternal = async (forceReopen = false) => {
    // Проверяем живое ли соединение
    if (currentDb && !forceReopen) {
        const isAlive = await checkConnection();
        if (isAlive) {
            return currentDb;
        }
        console.log('[DB] Connection dead, reopening...');
        currentDb = null;
    }

    // Закрываем старое соединение если есть
    if (currentDb) {
        try {
            currentDb.close();
            console.log('[DB] Closed old connection');
        } catch (e) {
            console.error('[DB] Error closing:', e);
        }
        currentDb = null;
    }

    // Открываем БД с обработкой ошибок
    try {
        console.log('[DB] Opening new connection...');

        // Апгрейд версии не пройдёт, пока БД открыта в другой вкладке. Раньше
        // промис в этом случае не резолвился никогда, и экран крутил спиннер
        // без объяснений. Падаем сразу по blocked, а таймаут — только страховка
        // на случай, если blocked почему-то не придёт.
        //
        // Ждать вслепую нельзя: сам запрос к IndexedDB после таймаута остаётся
        // висеть, и каждая перезагрузка добавляла бы ещё один в очередь.
        let rejectOpen;
        const openFailed = new Promise((_, reject) => { rejectOpen = reject; });
        const openTimer = setTimeout(
            () => rejectOpen(new Error('Timed out opening the local database. Close other tabs with this app and reload.')),
            15000
        );

        currentDb = await Promise.race([openFailed, openDB(DB_NAME, DB_VERSION, {
            blocked() {
                console.error('[DB] Database blocked by another connection');
                currentDb = null;
                rejectOpen(new Error('The app is open in another tab. Close it and reload — the local database needs to update.'));
            },
            blocking() {
                console.warn('[DB] Database blocking other connections');
                if (currentDb) {
                    currentDb.close();
                    currentDb = null;
                }
            },
            terminated() {
                console.error('[DB] Database connection terminated unexpectedly');
                currentDb = null;
            },
            upgrade(db, oldVersion, newVersion, transaction) {
                console.log(`IndexedDB upgrade: from version ${oldVersion} to ${newVersion}`);

                // USER_STORE
                if (!db.objectStoreNames.contains(USER_STORE)) {
                    db.createObjectStore(USER_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // SignedDocumentsDB - signedDocuments
                if (!db.objectStoreNames.contains(SIGNED_DOCS_STORE)) {
                    db.createObjectStore(SIGNED_DOCS_STORE, { keyPath: 'uniqueId', autoIncrement: true });
                }

                // SyncDB - employees
                if (!db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                    db.createObjectStore(EMPLOYEE_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Vendors
                if (!db.objectStoreNames.contains(VENDOR_STORE)) {
                    db.createObjectStore(VENDOR_STORE, { keyPath: 'id' });
                }

                // Trucks
                if (!db.objectStoreNames.contains(TRUCK_STORE)) {
                    db.createObjectStore(TRUCK_STORE, { keyPath: 'id' });
                }

                // SyncDB - instructions
                if (!db.objectStoreNames.contains(INSTRUCTION_STORE)) {
                    db.createObjectStore(INSTRUCTION_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // ToolBoxTalks - новая таблица для обновленной структуры
                if (!db.objectStoreNames.contains(TOOLBOXTALK_STORE)) {
                    db.createObjectStore(TOOLBOXTALK_STORE, { keyPath: 'id', autoIncrement: false });
                }

                // WeeklyInstructionsDB - weeklyInstructions
                if (!db.objectStoreNames.contains(WEEKLY_INSTRUCTION_STORE)) {
                    db.createObjectStore(WEEKLY_INSTRUCTION_STORE, { keyPath: 'uniqueId', autoIncrement: true });
                }

                // WeeklyListDB - weeklyList
                if (!db.objectStoreNames.contains(WEEK_LIST_STORE)) {
                    db.createObjectStore(WEEK_LIST_STORE, { keyPath: 'weekYear' });
                } else if (oldVersion < 20 && db.objectStoreNames.contains(WEEK_LIST_STORE)) {
                    // Если обновляемся на версию 20, пересоздаем store с правильным keyPath
                    db.deleteObjectStore(WEEK_LIST_STORE);
                    db.createObjectStore(WEEK_LIST_STORE, { keyPath: 'weekYear' });
                }

                // trainingDB - training
                if (!db.objectStoreNames.contains(TRAINING_STORE)) {
                    db.createObjectStore(TRAINING_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //mepForm
                if (!db.objectStoreNames.contains(MEP_FORM_STORE)) {
                    db.createObjectStore(MEP_FORM_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // ftForm
                if (!db.objectStoreNames.contains(FT_FORM_STORE)) {
                    db.createObjectStore(FT_FORM_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // trainingDue
                if (!db.objectStoreNames.contains(TRAINING_DUE_STORE)) {
                    db.createObjectStore(TRAINING_DUE_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // trainingDueTextStore
                if (!db.objectStoreNames.contains(TRAINING_DUE_TEXT_STORE)) {
                    db.createObjectStore(TRAINING_DUE_TEXT_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // costCode
                if (!db.objectStoreNames.contains(COST_CODE_STORE)) {
                    db.createObjectStore(COST_CODE_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // GLCode
                if (!db.objectStoreNames.contains(GL_CODE_STORE)) {
                    db.createObjectStore(GL_CODE_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // CC
                if (!db.objectStoreNames.contains(CC_STORE)) {
                    db.createObjectStore(CC_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // CODE_GROUP_STORE
                if (!db.objectStoreNames.contains(CODE_GROUP_STORE)) {
                    db.createObjectStore(CODE_GROUP_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // JOB_STORE
                if (!db.objectStoreNames.contains(JOB_STORE)) {
                    db.createObjectStore(JOB_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // TRAINING_DUE_FORM_STORE
                if (!db.objectStoreNames.contains(TRAINING_DUE_FORM_STORE)) {
                    db.createObjectStore(TRAINING_DUE_FORM_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // WELDING FORM STORE
                if (!db.objectStoreNames.contains(WELDING_FORM_STORE)) {
                    db.createObjectStore(WELDING_FORM_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // INI
                if (!db.objectStoreNames.contains(INI_STORE)) {
                    db.createObjectStore(INI_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Safety Stand Down
                if (!db.objectStoreNames.contains(SSD_STORE)) {
                    db.createObjectStore(SSD_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Job Hazard Analysis
                if (!db.objectStoreNames.contains(JHA_STORE)) {
                    db.createObjectStore(JHA_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Energy
                if (!db.objectStoreNames.contains(ENERGY_STORE)) {
                    db.createObjectStore(ENERGY_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Conditions
                if (!db.objectStoreNames.contains(CONDITIONS_STORE)) {
                    db.createObjectStore(CONDITIONS_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //DocsPermits
                if (!db.objectStoreNames.contains(DOCS_PERMIT_STORE)) {
                    db.createObjectStore(DOCS_PERMIT_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //EvaluationOfWork
                if (!db.objectStoreNames.contains(EVALUATION_OF_WORK_STORE)) {
                    db.createObjectStore(EVALUATION_OF_WORK_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Job Task
                if (!db.objectStoreNames.contains(JOB_TASK_STORE)) {
                    db.createObjectStore(JOB_TASK_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //WorkActivities
                if (!db.objectStoreNames.contains(WORK_ACTIVITIES_STORE)) {
                    db.createObjectStore(WORK_ACTIVITIES_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Temps employee
                if (!db.objectStoreNames.contains(TEMPS_STORE)) {
                    db.createObjectStore(TEMPS_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Attendance-коды из админки
                if (!db.objectStoreNames.contains(ATTEND_CODES_STORE)) {
                    db.createObjectStore(ATTEND_CODES_STORE, { keyPath: 'value' });
                }

                //Temp Agency
                if (!db.objectStoreNames.contains(TEMP_AGENCY_STORE)) {
                    db.createObjectStore(TEMP_AGENCY_STORE, { keyPath: 'id', autoIncrement: true });
                }

                //Hold Harmless Forms
                if (!db.objectStoreNames.contains(TEMP_EXHIBIT_B_STORE)) {
                    const store = db.createObjectStore(TEMP_EXHIBIT_B_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('agencyName', 'agencyName', { unique: false });
                }

                if (!db.objectStoreNames.contains(TRAINER_STORE)) {
                    db.createObjectStore(TRAINER_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Daily Logs
                if (!db.objectStoreNames.contains(DAILY_LOG_STORE)) {
                    const store = db.createObjectStore(DAILY_LOG_STORE, { keyPath: 'localId' });
                    store.createIndex('date', 'date', { unique: false });
                    store.createIndex('syncStatus', 'syncStatus', { unique: false });
                    store.createIndex('createdBy', 'createdBy', { unique: false });
                    store.createIndex('personId', 'personId', { unique: false });
                    store.createIndex('id', 'id', { unique: false });
                }

                // Incidents
                if (!db.objectStoreNames.contains(INCIDENT_STORE)) {
                    const store = db.createObjectStore(INCIDENT_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('uuid', 'uuid', { unique: true });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Witness statements
                if (!db.objectStoreNames.contains(WITNESS_STORE)) {
                    const witnessStore = db.createObjectStore(WITNESS_STORE, { keyPath: 'id' });
                    witnessStore.createIndex('incidentId', 'incidentId', { unique: false });
                    witnessStore.createIndex('status', 'status', { unique: false });
                }

                // Unified Forms (generic offline queue for UnifiedForm API)
                if (!db.objectStoreNames.contains(UNIFIED_FORM_STORE)) {
                    const store = db.createObjectStore(UNIFIED_FORM_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('type', 'type', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // TBT Submissions (ToolBoxTalk form submissions)
                if (!db.objectStoreNames.contains(TBT_SUBMISSION_STORE)) {
                    const store = db.createObjectStore(TBT_SUBMISSION_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('topicId', 'topicId', { unique: false });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Pipe Hydro Test forms
                if (!db.objectStoreNames.contains(PIPE_HYDRO_TEST_STORE)) {
                    const store = db.createObjectStore(PIPE_HYDRO_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Vessel Hydro Test forms
                if (!db.objectStoreNames.contains(VESSEL_HYDRO_TEST_STORE)) {
                    const store = db.createObjectStore(VESSEL_HYDRO_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Service Leak Test forms
                if (!db.objectStoreNames.contains(SERVICE_LEAK_TEST_STORE)) {
                    const store = db.createObjectStore(SERVICE_LEAK_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Weld Map forms
                if (!db.objectStoreNames.contains(WELD_MAP_STORE)) {
                    const store = db.createObjectStore(WELD_MAP_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Pneumatic Test forms
                if (!db.objectStoreNames.contains(PNEUMATIC_TEST_STORE)) {
                    const store = db.createObjectStore(PNEUMATIC_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Torque Map forms
                if (!db.objectStoreNames.contains(TORQUE_MAP_STORE)) {
                    const store = db.createObjectStore(TORQUE_MAP_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Gas Line Inspection forms
                if (!db.objectStoreNames.contains(GAS_LINE_INSPECTION_STORE)) {
                    const store = db.createObjectStore(GAS_LINE_INSPECTION_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Megger Test forms
                if (!db.objectStoreNames.contains(MEGGER_TEST_STORE)) {
                    const store = db.createObjectStore(MEGGER_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Terminal Torque Test forms
                if (!db.objectStoreNames.contains(TERMINAL_TORQUE_TEST_STORE)) {
                    const store = db.createObjectStore(TERMINAL_TORQUE_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Panel Shop QC Checklist forms
                if (!db.objectStoreNames.contains(PANEL_SHOP_QC_STORE)) {
                    const store = db.createObjectStore(PANEL_SHOP_QC_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Job Site Safety Inspection forms
                if (!db.objectStoreNames.contains(JOB_SITE_SAFETY_INSPECTION_STORE)) {
                    const store = db.createObjectStore(JOB_SITE_SAFETY_INSPECTION_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobSite', 'jobSite', { unique: false });
                    store.createIndex('date', 'date', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Published Job Site Safety Inspection question schemas, keyed by revision number.
                // Every revision the device has seen is kept, so reopening an old draft renders the
                // questions that draft was actually started on rather than today's wording.
                if (!db.objectStoreNames.contains(JSSI_SCHEMA_STORE)) {
                    db.createObjectStore(JSSI_SCHEMA_STORE, { keyPath: 'version' });
                }

                // Operator Practical Evaluation forms
                if (!db.objectStoreNames.contains(OPERATOR_PRACTICAL_EVALUATION_STORE)) {
                    const store = db.createObjectStore(OPERATOR_PRACTICAL_EVALUATION_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('date', 'date', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Aerial/Scissor Lift Operator Practical Evaluation forms
                if (!db.objectStoreNames.contains(AERIAL_SCISSOR_LIFT_OPERATOR_STORE)) {
                    const store = db.createObjectStore(AERIAL_SCISSOR_LIFT_OPERATOR_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('date', 'date', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Bus Inspection Report forms
                if (!db.objectStoreNames.contains(BUS_INSPECTION_STORE)) {
                    const store = db.createObjectStore(BUS_INSPECTION_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Cable Wiring Schedule forms
                if (!db.objectStoreNames.contains(CABLE_WIRING_SCHEDULE_STORE)) {
                    const store = db.createObjectStore(CABLE_WIRING_SCHEDULE_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                if (!db.objectStoreNames.contains(ENERGIZED_WORK_PERMIT_STORE)) {
                    const store = db.createObjectStore(ENERGIZED_WORK_PERMIT_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Change Order / Extra Work forms
                if (!db.objectStoreNames.contains(CHANGE_ORDER_STORE)) {
                    const store = db.createObjectStore(CHANGE_ORDER_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Scope of Work forms
                if (!db.objectStoreNames.contains(SOW_STORE)) {
                    const store = db.createObjectStore(SOW_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Spotter Training forms
                if (!db.objectStoreNames.contains(SPOTTER_TRAINING_STORE)) {
                    const store = db.createObjectStore(SPOTTER_TRAINING_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('spotterName', 'spotterName', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Packing Slip forms
                if (!db.objectStoreNames.contains(PACKING_SLIP_STORE)) {
                    const store = db.createObjectStore(PACKING_SLIP_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobNumber', 'jobNumber', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Confined Space Entry Permit forms
                if (!db.objectStoreNames.contains(CSEP_STORE)) {
                    const store = db.createObjectStore(CSEP_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Lock-Tag-Try Permit forms
                if (!db.objectStoreNames.contains(LTT_STORE)) {
                    const store = db.createObjectStore(LTT_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Continuous Improvement
                if (!db.objectStoreNames.contains(CONTINUOUS_IMPROVEMENT_STORE)) {
                    db.createObjectStore(CONTINUOUS_IMPROVEMENT_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Warranty forms
                if (!db.objectStoreNames.contains(WARRANTY_STORE)) {
                    const store = db.createObjectStore(WARRANTY_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('jobCode', 'jobCode', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Equipment
                if (!db.objectStoreNames.contains(EQUIPMENT_STORE)) {
                    db.createObjectStore(EQUIPMENT_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Equipment Locations
                if (!db.objectStoreNames.contains(EQUIP_LOCATION_STORE)) {
                    db.createObjectStore(EQUIP_LOCATION_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Purchase Orders
                if (!db.objectStoreNames.contains(PURCHASE_ORDER_STORE)) {
                    db.createObjectStore(PURCHASE_ORDER_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // PO Edits (offline pending edits queue)
                if (!db.objectStoreNames.contains(PO_EDITS_STORE)) {
                    const poEditsStore = db.createObjectStore(PO_EDITS_STORE, { keyPath: 'localId', autoIncrement: true });
                    poEditsStore.createIndex('status', 'status', { unique: false });
                    poEditsStore.createIndex('purchaseOrderId', 'purchaseOrderId', { unique: false });
                }

                // Practical Evaluation Types (synced from server)
                if (!db.objectStoreNames.contains(PRACTICAL_EVALUATION_TYPE_STORE)) {
                    const store = db.createObjectStore(PRACTICAL_EVALUATION_TYPE_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('slug', 'slug', { unique: true });
                    store.createIndex('isActive', 'isActive', { unique: false });
                }

                // Practical Evaluation forms (local drafts)
                if (!db.objectStoreNames.contains(PRACTICAL_EVALUATION_STORE)) {
                    const store = db.createObjectStore(PRACTICAL_EVALUATION_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('evaluationTypeSlug', 'evaluationTypeSlug', { unique: false });
                    store.createIndex('date', 'date', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Tool Inventory (synced from external VB6 DB) — legacy, used by Gas Monitor form
                if (!db.objectStoreNames.contains(TOOL_INVENTORY_STORE)) {
                    const store = db.createObjectStore(TOOL_INVENTORY_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('part', 'part', { unique: false });
                    store.createIndex('type', 'type', { unique: false });
                    store.createIndex('inventoryStatus', 'inventoryStatus', { unique: false });
                }

                // Tool (new local Symfony entity, /api/tool) — used by SRD form
                if (!db.objectStoreNames.contains(TOOL_STORE)) {
                    const store = db.createObjectStore(TOOL_STORE, { keyPath: 'id' });
                    store.createIndex('part', 'part', { unique: false });
                    store.createIndex('type', 'type', { unique: false });
                    store.createIndex('status', 'status', { unique: false });
                }

                // Tool Calibration Records
                if (!db.objectStoreNames.contains(TOOL_CALIBRATION_STORE)) {
                    const store = db.createObjectStore(TOOL_CALIBRATION_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('part', 'part', { unique: false });
                }

                // Tool Docs
                if (!db.objectStoreNames.contains(TOOL_DOCS_STORE)) {
                    const store = db.createObjectStore(TOOL_DOCS_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('part', 'part', { unique: false });
                }

                // Tool Assignments
                if (!db.objectStoreNames.contains(TOOL_ASSIGNMENT_STORE)) {
                    const store = db.createObjectStore(TOOL_ASSIGNMENT_STORE, { keyPath: 'id', autoIncrement: true });
                    store.createIndex('part', 'part', { unique: false });
                }

                // Gas Monitor Test (local drafts/submissions)
                if (!db.objectStoreNames.contains(GAS_MONITOR_TEST_STORE)) {
                    const store = db.createObjectStore(GAS_MONITOR_TEST_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('operationType', 'operationType', { unique: false });
                    store.createIndex('toolType', 'toolType', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // External Equipment Inspections (local drafts)
                if (!db.objectStoreNames.contains(EXT_EQUIPMENT_INSPECTION_STORE)) {
                    const store = db.createObjectStore(EXT_EQUIPMENT_INSPECTION_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('qrToken', 'qrToken', { unique: false });
                    store.createIndex('formType', 'formType', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // External Equipment Daily Checks (local drafts)
                if (!db.objectStoreNames.contains(EXT_EQUIPMENT_DAILY_CHECK_STORE)) {
                    const store = db.createObjectStore(EXT_EQUIPMENT_DAILY_CHECK_STORE, { keyPath: 'uuid' });
                    store.createIndex('status', 'status', { unique: false });
                    store.createIndex('qrToken', 'qrToken', { unique: false });
                    store.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // SRD Inspection (local drafts)
                if (!db.objectStoreNames.contains(SRD_INSPECTION_STORE)) {
                    const srdStore = db.createObjectStore(SRD_INSPECTION_STORE, { keyPath: 'uuid' });
                    srdStore.createIndex('status', 'status', { unique: false });
                    srdStore.createIndex('toolPart', 'toolPart', { unique: false });
                    srdStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Fall Arrest Rescue Plan (local drafts + submitted mirror)
                if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) {
                    const farpStore = db.createObjectStore(FALL_ARREST_RESCUE_PLAN_STORE, { keyPath: 'uuid' });
                    farpStore.createIndex('status', 'status', { unique: false });
                    farpStore.createIndex('jobCode', 'jobCode', { unique: false });
                    farpStore.createIndex('planDate', 'planDate', { unique: false });
                    farpStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // On-Site Welder Qualification & Release (LC-36A) — только локальные
                // черновики: после успешной отправки запись удаляется из стора,
                // форма живёт дальше только в Symfony.
                if (!db.objectStoreNames.contains(WELDER_QUALIFICATION_RELEASE_STORE)) {
                    const wqrStore = db.createObjectStore(WELDER_QUALIFICATION_RELEASE_STORE, { keyPath: 'uuid' });
                    wqrStore.createIndex('status', 'status', { unique: false });
                    wqrStore.createIndex('jobCode', 'jobCode', { unique: false });
                    wqrStore.createIndex('releaseDate', 'releaseDate', { unique: false });
                    wqrStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Lift Evaluation Form (LEF) — так же только локальные черновики:
                // после отправки запись удаляется, форма живёт в Symfony.
                if (!db.objectStoreNames.contains(LIFT_EVALUATION_STORE)) {
                    const lefStore = db.createObjectStore(LIFT_EVALUATION_STORE, { keyPath: 'uuid' });
                    lefStore.createIndex('status', 'status', { unique: false });
                    lefStore.createIndex('jobCode', 'jobCode', { unique: false });
                    lefStore.createIndex('liftDate', 'liftDate', { unique: false });
                    lefStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // Lift Truck PM Inspection — только локальные черновики: после
                // отправки запись удаляется, форма живёт в Symfony.
                if (!db.objectStoreNames.contains(LIFT_TRUCK_PM_STORE)) {
                    const ltpmStore = db.createObjectStore(LIFT_TRUCK_PM_STORE, { keyPath: 'uuid' });
                    ltpmStore.createIndex('status', 'status', { unique: false });
                    ltpmStore.createIndex('equipmentCode', 'equipmentCode', { unique: false });
                    ltpmStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // JHA Document Triggers — универсальные триггеры для ЛЮБОГО документа
                // (DocsPermits + спец-типы вроде FARP) с поддержкой до 3 conditions
                // (WorkActivity + EnergySource + Hazard). Синхронизируются с сервера
                // через incremental sync (lastSyncTimestamp). keyPath = 'id' (PK на сервере).
                if (db.objectStoreNames.contains('fallArrestRescuePlanTriggers')) {
                    db.deleteObjectStore('fallArrestRescuePlanTriggers');
                }
                if (!db.objectStoreNames.contains(JHA_DOCUMENT_TRIGGER_STORE)) {
                    db.createObjectStore(JHA_DOCUMENT_TRIGGER_STORE, { keyPath: 'id' });
                }

                // Training Sessions (Demo) — synced courses & quizzes + initiator's sessions.
                // Курс-стор ключуется по внутреннему id (а не externalId): локальные курсы
                // не имеют externalId, и ключ-null сломал бы store.put при синке.
                if (!db.objectStoreNames.contains(TRAINING_COURSE_STORE)) {
                    db.createObjectStore(TRAINING_COURSE_STORE, { keyPath: 'id' });
                } else if (oldVersion < 96) {
                    db.deleteObjectStore(TRAINING_COURSE_STORE);
                    db.createObjectStore(TRAINING_COURSE_STORE, { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains(TRAINING_QUIZ_STORE)) {
                    const tqStore = db.createObjectStore(TRAINING_QUIZ_STORE, { keyPath: 'id' });
                    tqStore.createIndex('byCourse', 'courseExternalId', { unique: false });
                }
                if (!db.objectStoreNames.contains(TRAINING_SESSION_STORE)) {
                    const tsStore = db.createObjectStore(TRAINING_SESSION_STORE, { keyPath: 'uuid' });
                    tsStore.createIndex('byStatus', 'status', { unique: false });
                    tsStore.createIndex('byCreatedAt', 'createdAt', { unique: false });
                }
                // Офлайн-очередь мутаций тренинг-сессий (create/addParticipant/markPassed).
                // Проигрывается на сервер при появлении сети (см. trainingSessionService).
                if (!db.objectStoreNames.contains(TRAINING_OUTBOX_STORE)) {
                    const outbox = db.createObjectStore(TRAINING_OUTBOX_STORE, { keyPath: 'opId' });
                    outbox.createIndex('byCreatedAt', 'createdAt', { unique: false });
                }

                // Safety Scheduling: что рэпу назначено по дням. Ключ — 'slot-N' для
                // строки из расписания и 'visit-N' для локации, которую он добавил сам.
                if (!db.objectStoreNames.contains(SAFETY_SCHEDULE_STORE)) {
                    const ssStore = db.createObjectStore(SAFETY_SCHEDULE_STORE, { keyPath: 'id' });
                    ssStore.createIndex('byWorkDate', 'workDate', { unique: false });
                }
                // Очередь отметок, поставленных без сети. Проигрывается на сервер,
                // когда связь появится (см. safetyScheduleService).
                if (!db.objectStoreNames.contains(SAFETY_VISIT_OUTBOX_STORE)) {
                    const ssOutbox = db.createObjectStore(SAFETY_VISIT_OUTBOX_STORE, { keyPath: 'opId' });
                    ssOutbox.createIndex('byCreatedAt', 'createdAt', { unique: false });
                }

                // Alternative Jobs (synced from external AltJob table)
                if (!db.objectStoreNames.contains(ALT_JOB_STORE)) {
                    db.createObjectStore(ALT_JOB_STORE, { keyPath: 'id', autoIncrement: true });
                }

                // Timesheet Records
                if (!db.objectStoreNames.contains(TIMESHEET_RECORD_STORE)) {
                    const tsStore = db.createObjectStore(TIMESHEET_RECORD_STORE, { keyPath: 'clientId' });
                    tsStore.createIndex('byDate', 'workDate', { unique: false });
                    tsStore.createIndex('byStatus', 'status', { unique: false });
                    tsStore.createIndex('byEmployee', 'employeeId', { unique: false });
                    tsStore.createIndex('bySyncStatus', 'syncStatus', { unique: false });
                    tsStore.createIndex('byEmployeeAndDate', 'employeeDate', { unique: false });
                } else {
                    const tsStore = transaction.objectStore(TIMESHEET_RECORD_STORE);
                    if (!tsStore.indexNames.contains('bySyncStatus')) {
                        tsStore.createIndex('bySyncStatus', 'syncStatus', { unique: false });
                    }
                    if (!tsStore.indexNames.contains('byEmployeeAndDate')) {
                        tsStore.createIndex('byEmployeeAndDate', 'employeeDate', { unique: false });
                    }
                }

                // Daily Job Notes
                if (!db.objectStoreNames.contains(DAILY_JOB_NOTE_STORE)) {
                    const djnStore = db.createObjectStore(DAILY_JOB_NOTE_STORE, { keyPath: 'clientId' });
                    djnStore.createIndex('byDate', 'noteDate', { unique: false });
                    djnStore.createIndex('byStatus', 'status', { unique: false });
                    djnStore.createIndex('bySyncStatus', 'syncStatus', { unique: false });
                } else {
                    const djnStore = transaction.objectStore(DAILY_JOB_NOTE_STORE);
                    if (!djnStore.indexNames.contains('bySyncStatus')) {
                        djnStore.createIndex('bySyncStatus', 'syncStatus', { unique: false });
                    }
                }

                // Signed Timesheets
                if (!db.objectStoreNames.contains(SIGNED_TIMESHEET_STORE)) {
                    const stStore = db.createObjectStore(SIGNED_TIMESHEET_STORE, { keyPath: 'clientId' });
                    stStore.createIndex('byStatus', 'status', { unique: false });
                    stStore.createIndex('bySyncStatus', 'syncStatus', { unique: false });
                } else {
                    const stStore = transaction.objectStore(SIGNED_TIMESHEET_STORE);
                    if (!stStore.indexNames.contains('bySyncStatus')) {
                        stStore.createIndex('bySyncStatus', 'syncStatus', { unique: false });
                    }
                }

                // Selected Employees (per date)
                if (!db.objectStoreNames.contains(SELECTED_EMPLOYEES_STORE)) {
                    db.createObjectStore(SELECTED_EMPLOYEES_STORE, { keyPath: 'date' });
                }

                // Selected Jobs (per date)
                if (!db.objectStoreNames.contains(SELECTED_JOBS_STORE)) {
                    db.createObjectStore(SELECTED_JOBS_STORE, { keyPath: 'date' });
                }

                // Safety Data Sheets (synced from external SDS table, online PDF view)
                if (!db.objectStoreNames.contains(SDS_STORE)) {
                    const sdsStore = db.createObjectStore(SDS_STORE, { keyPath: 'id' });
                    sdsStore.createIndex('category', 'category', { unique: false });
                    sdsStore.createIndex('name', 'name', { unique: false });
                }

            },
        })]);

        clearTimeout(openTimer);

        // Запускаем проверку каждые 60 секунд
        if (!keepAliveTimer) {
            keepAliveTimer = setInterval(async () => {
                const isAlive = await checkConnection();
                if (!isAlive) {
                    console.log('[DB] Keep-alive check failed, connection will be reopened on next use');
                    currentDb = null;
                }
            }, 60000); // 60 секунд
        }

        console.log('[DB] Connection opened successfully');
        return currentDb;
    } catch (error) {
        console.error('[DB] Failed to open database:', error);
        currentDb = null;
        throw error;
    }
};

/**
 * Закрыть соединение к БД и остановить keep-alive таймер
 */
export const closeDB = () => {
    console.log('[DB] Closing database connection...');
    if (keepAliveTimer) {
        clearInterval(keepAliveTimer);
        keepAliveTimer = null;
        console.log('[DB] Keep-alive timer stopped');
    }
    if (currentDb) {
        try {
            currentDb.close();
            console.log('[DB] Database connection closed');
        } catch (e) {
            console.error('[DB] Error closing database:', e);
        }
        currentDb = null;
    }
};


/**
 * Получить одну запись по ID
 * @param {string} storeName
 * @param {number|string} id
 */
export async function getById(storeName, id) {
    const db = await initializeDB();
    return db.get(storeName, id);
}

/**
 * Добавить или обновить запись (put)
 * @param {string} storeName
 * @param {object} record
 */
export async function addOrUpdate(storeName, record) {
    const db = await initializeDB();
    return db.put(storeName, record);
}

/**
 * Получить все записи из store
 * @param {string} storeName
 */
export async function getAll(storeName) {
    const db = await initializeDB();
    return db.getAll(storeName);
}
